'use es6';

import I18n from 'I18n';
import PropTypes from 'prop-types';
import { userInfoSync } from 'hub-http/userInfo';
import emptyFunction from 'react-utils/emptyFunction';
import { DefaultFieldKeys } from 'ExportDialog/Constants';
import FormatField from 'ExportDialog/modal/formFields/FormatField';
import createFormField from 'ExportDialog/modal/formFields/FormField';
import { growl } from 'EmailComponents/errors/growl';
import ManageExportTimeRangeSelect from 'email-management-lib/refactor/components/manage/ManageExportTimeRangeSelect';
import makeReadOnlyEmailField from 'EmailComponents/utils/makeReadOnlyEmailField';
import { ALL_AVAILABLE_EXTRA_FIELDS, AVAILABLE_EXTRA_FIELDS, BUSINESS_UNIT_EXPORT_FIELD } from 'email-management-lib/refactor/constants/exportConstants';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import ExportDialog from 'ExportDialog/modal/ExportDialog';
import IncludeAllColumnsFormField from 'email-management-lib/refactor/components/manage/frameworkTable/components/IncludeAllColumnsFormField';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { BusinessUnitsRebrandMessage } from 'ui-business-units-lib/components/BusinessUnitsRebrandMessage';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ExportClient = doExport => {
  return {
    export(data) {
      return doExport(data);
    }
  };
};
function Bold({
  children
}) {
  return /*#__PURE__*/_jsx("strong", {
    children: children
  });
}
const ExportDescription = () => {
  return /*#__PURE__*/_jsx(View, {
    marginBlockEnd: "500",
    as: "div",
    children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      elements: {
        Bold
      },
      message: "email-management.modals.export.description_jsx"
    })
  }, "description");
};
const ExportName = ({
  value,
  onChange
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    label: I18n.text('email-management.modals.export.exportNameLabel'),
    children: /*#__PURE__*/_jsx(TextInput, {
      autoFocus: true,
      defaultValue: I18n.text('email-management.modals.export.exportDefaultName'),
      onChange: e => onChange(e.target.value),
      value: value
    })
  });
};
const AdvancedFieldsList = (fields = [], isFree) => {
  const renderAdvancedHelpIcon = () => {
    return /*#__PURE__*/_jsx(HelpIcon, {
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "email-management.modals.export.advancedHelpIcon"
      }),
      marginInlineStart: "100"
    });
  };
  const renderDisabledAdvancedField = () => {
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: "top",
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "email-management.modals.export.disabledIncludeAdvanced"
      }),
      children: /*#__PURE__*/_jsxs(Checkbox, {
        "data-test": "include-advanced",
        disabled: true,
        children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "email-management.modals.export.includeAdvanced"
        }), renderAdvancedHelpIcon()]
      })
    }, "include-advanced");
  };
  const renderAdvancedField = (value = {}, onChange) => {
    return /*#__PURE__*/_jsxs(Checkbox, {
      checked: value.includeAdvanced,
      "data-test": "include-advanced",
      onChange: event => onChange(Object.assign({}, value, {
        includeAdvanced: event.target.checked
      })),
      children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "email-management.modals.export.includeAdvanced"
      }), renderAdvancedHelpIcon()]
    }, "include-advanced");
  };
  const renderField = (field, value = {}, onChange) => {
    const {
      extraFields = []
    } = value;
    return /*#__PURE__*/_jsx(Checkbox, {
      checked: extraFields.includes(field),
      onChange: event => {
        const newExtraFields = event.target.checked ? [...extraFields, field] : extraFields.filter(v => v !== field);
        onChange(Object.assign({}, value, {
          extraFields: newExtraFields
        }));
      },
      children: field === 'BUSINESS_UNIT_NAME' ? /*#__PURE__*/_jsx(BusinessUnitsRebrandMessage, {
        message: "email-management.modals.export.includeBUSINESS_UNIT_NAME",
        rebrandMessage: "email-management.modals.export.includeBUSINESS_UNIT_NAME_buv2"
      }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `email-management.modals.export.include${field}`
      })
    }, field);
  };
  return ({
    value = {},
    onChange
  }) => {
    return /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "email-management.modals.export.extraFieldsTitle"
      }),
      marginBlockStart: "300",
      children: /*#__PURE__*/_jsxs(ToggleGroup, {
        "data-test": "advanced-options",
        name: "advanced-options",
        children: [isFree ? renderDisabledAdvancedField() : renderAdvancedField(value, onChange), fields.map(fieldName => renderField(fieldName, value, onChange))]
      })
    });
  };
};
const DashboardExportDialog = ({
  hasFullDashboardAccess,
  isFree,
  isUseInFramework,
  closeModal,
  userEmail,
  exportClient,
  canAccessBusinessUnit
}) => {
  const showSuccessMessage = () => {
    growl('success', I18n.text('email-management.modals.export.success_message'));
  };
  const AVAILABLE_EXPORT_FIELDS = isUseInFramework ? [] : hasFullDashboardAccess ? canAccessBusinessUnit ? [...ALL_AVAILABLE_EXTRA_FIELDS, ...BUSINESS_UNIT_EXPORT_FIELD] : ALL_AVAILABLE_EXTRA_FIELDS : AVAILABLE_EXTRA_FIELDS;
  return /*#__PURE__*/_jsx(ExportDialog, {
    client: exportClient,
    formFields: [createFormField(ExportDescription, 'description'), createFormField(ExportName, 'name'), createFormField(FormatField, DefaultFieldKeys.FORMAT), createFormField(makeReadOnlyEmailField(userEmail), DefaultFieldKeys.EMAIL), isUseInFramework ? IncludeAllColumnsFormField : undefined, createFormField(ManageExportTimeRangeSelect, 'timeRangeSelect'), createFormField(AdvancedFieldsList(AVAILABLE_EXPORT_FIELDS, isFree), 'advancedFields')].filter(Boolean),
    headerContents: I18n.text('email-management.modals.export.dialogName'),
    onClose: closeModal,
    onFailure: emptyFunction,
    onSuccess: showSuccessMessage,
    scrollHeight: 700,
    userEmail: userEmail
  });
};
function EmailExportDialog({
  hasFullDashboardAccess,
  isFree,
  isUseInFramework,
  doExport,
  canAccessBusinessUnit,
  closeModal
}) {
  const userEmail = userInfoSync().user.email;
  const exportClient = ExportClient(doExport);
  return /*#__PURE__*/_jsx(DashboardExportDialog, {
    canAccessBusinessUnit: canAccessBusinessUnit,
    closeModal: closeModal,
    exportClient: exportClient,
    hasFullDashboardAccess: hasFullDashboardAccess,
    isFree: isFree,
    isUseInFramework: isUseInFramework,
    userEmail: userEmail
  });
}
EmailExportDialog.propTypes = {
  canAccessBusinessUnit: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  doExport: PropTypes.func.isRequired,
  hasFullDashboardAccess: PropTypes.bool.isRequired,
  isFree: PropTypes.bool.isRequired,
  isUseInFramework: PropTypes.bool
};
export default EmailExportDialog;